import axios from 'axios';
import { apiCore } from './apiCore';
import apiConfig from './config';
import type { ContactListResponse } from '@/types/employee';

/**
 * 회의 초대 대상자 정보 (POST /v2/invite payload)
 * - USER: 위하고 회원 — companyNo, userNo, portalId 필수
 * - PHONE: 휴대폰 — phone 필수 (한국 휴대폰 형식, 하이픈 제외)
 * - EMAIL: 이메일 — email 필수
 */
export type InviteParticipant =
  | {
      type: 'USER';
      companyNo: number;
      userNo: number;
      portalId: string;
      userName?: string | null;
      profileUrl?: string | null;
    }
  | {
      type: 'PHONE';
      phone: string;
      userName?: string | null;
      profileUrl?: string | null;
    }
  | {
      type: 'EMAIL';
      email: string;
      userName?: string | null;
      profileUrl?: string | null;
    };

export const inviteAPI = {
  /**
   * 1. 회사 내의 조직 리스트 조회
   */
  getOrganizationTree: (cno: string, companyNo: string) => {
    const url = new URL(`${apiConfig.portal.wehagoCommonApiUrl}/company/organizationmanagement/tree`);
    url.searchParams.set('company_no', companyNo);
    url.searchParams.set('cno', cno);
    url.searchParams.set('containConnect', 'T');
    url.searchParams.set('portalIdYn', 'Y');
    url.searchParams.set('employeeStatus', '2');
    
    return axios.get(url.toString(), {
      headers: apiCore.getRequestHeaders(url.toString()) as any,
    }).then(res => res.data);
  },

  /**
   * 2. 조직 내의 직원 리스트 조회 (하위 조회 또는 전체 조회(is_group_organization))
   */
  getOrganizationEmployees: (cno: string, companyNo: string, organizationNo: string = '', isGroup: boolean = false) => {
    const url = new URL(`${apiConfig.portal.wehagoCommonApiUrl}/company/organizationmanagement/tree/employee`);
    url.searchParams.set('companyNo', companyNo);
    url.searchParams.set('cno', cno);
    url.searchParams.set('is_profile_view_option', 'T');
    url.searchParams.set('portalIdYn', 'Y');
    url.searchParams.set('employeeStatus', '2');
    
    if (organizationNo) {
      url.searchParams.set('organizationNo', organizationNo);
    }
    if (isGroup) {
      url.searchParams.set('is_group_organization', 'T');
    }

    return axios.get(url.toString(), {
      headers: apiCore.getRequestHeaders(url.toString()) as any,
    }).then(res => res.data);
  },

  /**
   * 3. 회사내의 키워드 검색 조회 (직원/연락처 탭 등에서 활용)
   */
  searchOrganizationEmployees: (cno: string, searchKeyword: string) => {
    const url = new URL(`${apiConfig.portal.wehagoCommonApiUrl}/company/organizationmanagement/tree/employee/search`);
    url.searchParams.set('cno', cno);
    url.searchParams.set('portalIdYn', 'Y');
    url.searchParams.set('employeeStatus', '2');
    url.searchParams.set('notEmployeeStatus', '4'); // 퇴사자 제외
    url.searchParams.set('searchKeyword', searchKeyword);

    return axios.get(url.toString(), {
      headers: apiCore.getRequestHeaders(url.toString()) as any,
    }).then(res => res.data);
  },

  /**
   * 4. 연락처 목록 조회/검색
   * @param keyword 검색어
   */
  getContactList: (keyword: string = ''): Promise<ContactListResponse> => {
    const url = new URL(`${apiConfig.contactsApiUrl}/list`);
    const params = new URLSearchParams();
    params.append('offsetNum', '0');
    params.append('limit_count', 'all');
    params.append('groupIdx', '-1');
    params.append('sortIdx', '-1');
    params.append('isAscending', 'T');
    params.append('searchType', 'CONTACT');
    params.append('keyword_name', keyword);

    return axios.post<ContactListResponse>(url.toString(), params, {
      headers: {
        ...apiCore.getRequestHeaders(url.toString()),
        'Content-Type': 'application/x-www-form-urlencoded'
      } as any,
    }).then(res => res.data);
  },

  /**
   * 5. 최근에 초대한 이메일 조회 (외부참여자 탭)
   */
  getRecentInviteEmails: () => {
    const url = new URL(`${apiConfig.weMeetAPI.baseApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/invite/recent/email`);
    const cno = apiCore.hSelectedCno;
    if (cno) url.searchParams.set('cno', cno);

    return axios.get(url.toString(), {
      headers: apiCore.getRequestHeaders(url.toString()) as any,
    }).then(res => res.data);
  },

  /**
   * 5. 회의 참석자 초대 전송 (POST)
   * @param roomId 회의방 ID (UUID)
   * @param participantList 초대 대상 목록 (USER/PHONE/EMAIL)
   */
  postInvite: (roomId: string, participantList: InviteParticipant[]) => {
    const url = new URL(`${apiConfig.weMeetAPI.baseApiUrl}${apiConfig.weMeetAPI.endpoint}/v2/invite`);
    url.searchParams.set('roomId', roomId);
    const cno = apiCore.hSelectedCno;
    if (cno) url.searchParams.set('cno', cno);

    const payload = { participantList };

    return axios.post(url.toString(), payload, {
      headers: apiCore.getRequestHeaders(url.toString()) as any,
    }).then(res => res.data);
  },

  /**
   * 6. 사용자 정보 조회 (네임카드용)
   * GET /common/user/userinfo — user_no로 단건 조회
   */
  getUserInfo: (cno: string, userNo: string) => {
    const url = new URL(`${apiConfig.portal.wehagoCommonApiUrl}/user/userinfo`);
    url.searchParams.set('cno', cno);
    url.searchParams.set('user_no', userNo);
    url.searchParams.set('is_include_withdrawal', 'F');

    return axios.get(url.toString(), {
      headers: apiCore.getRequestHeaders(url.toString()) as any,
    }).then(res => {
      const list = res.data?.resultData ?? [];
      if (list.length === 0) return null;
      const matched = list.find((item: any) => String(item.company_no) === String(cno));
      return matched ?? list[0];
    });
  },
};
